import React, { useCallback, useEffect, useState } from 'react';
import { Plus, History, ClipboardList, Trash2, Play, Pencil } from 'lucide-react';
import type {
  TrainingPlan,
  TrainingPlanPayload,
  TrainingDay,
  TrainingSessionExerciseLog,
} from '../types';
import {
  fetchTrainingPlans,
  createTrainingPlan,
  updateTrainingPlan,
  deleteTrainingPlan,
  saveTrainingSession,
  fetchAllTrainingSessions,
} from '../lib/trainingPlanApi';
import { emptyTrainingPlanPayload } from '../lib/trainingPlanNormalize';
import SectionBackButton from '../components/SectionBackButton';
import TrainingPlanEditor from './trainingPlan/TrainingPlanEditor';
import ActiveWorkoutSession from './trainingPlan/ActiveWorkoutSession';
import WorkoutHistoryList from './trainingPlan/WorkoutHistoryList';

type Screen = 'hub' | 'history' | 'editor' | 'pickDay' | 'active';

const MyTrainingPlan: React.FC = () => {
  const [screen, setScreen] = useState<Screen>('hub');
  const [plans, setPlans] = useState<TrainingPlan[]>([]);
  const [sessions, setSessions] = useState<Awaited<ReturnType<typeof fetchAllTrainingSessions>>>([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const [editPlanId, setEditPlanId] = useState<string | null>(null);
  const [editorName, setEditorName] = useState('');
  const [editorPayload, setEditorPayload] = useState<TrainingPlanPayload | null>(null);

  const [pickPlan, setPickPlan] = useState<TrainingPlan | null>(null);
  const [activePlan, setActivePlan] = useState<TrainingPlan | null>(null);
  const [activeDay, setActiveDay] = useState<TrainingDay | null>(null);
  /** Bump so ActiveWorkoutSession remounts each time we start the same day again */
  const [activeWorkoutKey, setActiveWorkoutKey] = useState(0);

  const loadPlans = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchTrainingPlans();
      setPlans(data);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load plans');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  const openHistory = async () => {
    setScreen('history');
    setHistoryLoading(true);
    setError(null);
    try {
      const [s, p] = await Promise.all([fetchAllTrainingSessions(), fetchTrainingPlans()]);
      setSessions(s);
      setPlans(p);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const openNewEditor = () => {
    setEditPlanId(null);
    setEditorName('');
    setEditorPayload(emptyTrainingPlanPayload());
    setScreen('editor');
    setError(null);
  };

  const openEdit = (p: TrainingPlan) => {
    setEditPlanId(p.id);
    setEditorName(p.name);
    setEditorPayload(JSON.parse(JSON.stringify(p.exercises)));
    setScreen('editor');
    setError(null);
  };

  const handleEditorSave = async (name: string, payload: TrainingPlanPayload) => {
    try {
      setSaving(true);
      if (editPlanId) {
        await updateTrainingPlan(editPlanId, name, payload);
      } else {
        await createTrainingPlan(name, payload);
      }
      await loadPlans();
      setScreen('hub');
      setEditorPayload(null);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleEditorBack = () => {
    setScreen('hub');
    setEditorPayload(null);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Delete this training plan?')) return;
    try {
      await deleteTrainingPlan(id);
      await loadPlans();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Delete failed');
    }
  };

  const openPickDay = (p: TrainingPlan) => {
    setPickPlan(p);
    setScreen('pickDay');
  };

  const startDay = (day: TrainingDay) => {
    if (!pickPlan) return;
    setActivePlan(pickPlan);
    setActiveDay(day);
    setPickPlan(null);
    setActiveWorkoutKey((k) => k + 1);
    setScreen('active');
  };

  const handleSessionFinish = async (
    log: TrainingSessionExerciseLog[],
    startedAtIso: string,
    endedAtIso: string
  ) => {
    const planId = activePlan?.id;
    const dayId = activeDay?.dayId;
    if (!planId || !dayId) return;
    try {
      await saveTrainingSession(planId, dayId, startedAtIso, endedAtIso, log);
      setActivePlan(null);
      setActiveDay(null);
      await loadPlans();
      setScreen('hub');
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'Could not save workout';
      setError(msg);
      window.alert(msg);
    }
  };

  if (screen === 'editor' && editorPayload) {
    return (
      <TrainingPlanEditor
        screenTitle={editPlanId ? 'Edit training plan' : 'New training plan'}
        initialName={editorName}
        initialPayload={editorPayload}
        onSave={handleEditorSave}
        onBack={handleEditorBack}
        saving={saving}
      />
    );
  }

  if (screen === 'history') {
    return (
      <>
        <SectionBackButton to="/gym" label="Back to Gym" />
        <WorkoutHistoryList
          sessions={sessions}
          plans={plans}
          loading={historyLoading}
          onBack={() => setScreen('hub')}
        />
      </>
    );
  }

  if (screen === 'pickDay' && pickPlan) {
    return (
      <>
        <SectionBackButton to="/gym" label="Back to Gym" />
        <div className="min-h-screen bg-theme-secondary pb-20">
        <div className="max-w-lg mx-auto px-4 py-8">
          <h2 className="text-xl font-bold text-theme-primary mb-2">Choose day</h2>
          <p className="text-sm text-theme-secondary mb-6">{pickPlan.name}</p>
          <ul className="space-y-2">
            {pickPlan.exercises.days.map((d) => (
              <li key={d.dayId}>
                <button
                  type="button"
                  onClick={() => startDay(d)}
                  className="w-full text-left px-4 py-4 rounded-xl bg-theme-primary border border-theme-primary hover:border-green-600 text-theme-primary font-medium"
                >
                  {d.title}
                  <span className="block text-xs text-theme-secondary font-normal mt-1">
                    {d.exercises.length} exercise{d.exercises.length !== 1 ? 's' : ''}
                  </span>
                </button>
              </li>
            ))}
          </ul>
          <button
            type="button"
            onClick={() => {
              setPickPlan(null);
              setScreen('hub');
            }}
            className="mt-6 text-sm text-theme-secondary hover:text-theme-primary"
          >
            Cancel
          </button>
        </div>
        </div>
      </>
    );
  }

  if (screen === 'active' && activePlan && activeDay) {
    return (
      <ActiveWorkoutSession
        key={`${activePlan.id}-${activeDay.dayId}-${activeWorkoutKey}`}
        planName={activePlan.name}
        dayTitle={activeDay.title}
        exercises={activeDay.exercises}
        onFinish={(log, started, ended) => handleSessionFinish(log, started, ended)}
        onExit={() => {
          if (window.confirm('Leave without saving this workout?')) {
            setActivePlan(null);
            setActiveDay(null);
            setScreen('hub');
          }
        }}
      />
    );
  }

  return (
    <>
      <SectionBackButton to="/gym" label="Back to Gym" />
      <div className="min-h-screen bg-theme-secondary pb-20">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
        <h1 className="text-3xl font-bold text-theme-primary mb-2">My Training Plan</h1>
        <p className="text-theme-secondary mb-6">
          Create multi-day programs and log workouts with a timer.
        </p>

        {error && (
          <div className="bg-red-500/10 text-red-400 rounded-lg p-3 text-sm mb-4">{error}</div>
        )}

        <div className="flex flex-col sm:flex-row gap-3 mb-6">
          <button
            type="button"
            onClick={openNewEditor}
            className="flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 text-white font-medium py-3 rounded-lg transition-colors"
          >
            <Plus className="w-5 h-5" />
            New training plan
          </button>
          <button
            type="button"
            onClick={openHistory}
            className="flex-1 flex items-center justify-center gap-2 bg-theme-primary border border-theme-primary hover:border-green-600 text-theme-primary font-medium py-3 rounded-lg transition-colors"
          >
            <History className="w-5 h-5" />
            Workout history
          </button>
        </div>

        {loading ? (
          <p className="text-center text-theme-secondary py-12">Loading…</p>
        ) : plans.length === 0 ? (
          <p className="text-center text-theme-secondary py-12">No training plans yet.</p>
        ) : (
          <div className="space-y-3">
            {plans.map((p) => (
              <div
                key={p.id}
                className="bg-theme-primary rounded-lg border border-theme-primary p-4 shadow-sm"
              >
                <div className="flex items-start gap-3">
                  <ClipboardList className="w-8 h-8 text-green-500 shrink-0 mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <h3 className="font-semibold text-theme-primary text-lg">{p.name}</h3>
                    <p className="text-xs text-theme-secondary mt-1">
                      {p.exercises.days.length} day{p.exercises.days.length !== 1 ? 's' : ''} ·{' '}
                      {p.exercises.days.reduce((n, d) => n + d.exercises.length, 0)} total exercises
                    </p>
                    <div className="flex flex-wrap gap-2 mt-3">
                      <button
                        type="button"
                        onClick={() => openEdit(p)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-theme-secondary text-sm text-theme-primary hover:bg-green-600/20"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                        Edit plan
                      </button>
                      <button
                        type="button"
                        onClick={() => openPickDay(p)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-green-600 text-sm text-white hover:bg-green-500"
                      >
                        <Play className="w-3.5 h-3.5" />
                        Start workout
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(p.id)}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-3.5 h-3.5" />
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      </div>
    </>
  );
};

export default MyTrainingPlan;
